import { allUsers, updateUserFields, orgSignatureConfig, saveOrgSignature, pushSignatures } from '../db.js';
import { escapeHtml, titleCase, initials, skeleton } from '../ui.js';
import { openOverlay } from './overlay.js';
import { openDialog, openPanel } from './dialog.js';
import { ukMobile } from '../uk-mobile.js';
import { previewDocument, IMAGE_STATES, toE164 } from '../signature-render.js';

// TEAM → SIGNATURE (Karan's spec, 07/09/2026).
//
// The one screen where the practice decides what every outgoing email says
// underneath it. Track holds the words; a Cloud Function writes them into each
// person's Gmail (functions/signature/).
//
// TWO THINGS THIS SCREEN IS BUILT AROUND:
//
// 1. NOTHING IS PUSHED WITHOUT BEING READ FIRST. "Preview all" is a dry run —
//    it renders every signature server-side and writes nothing — and it is the
//    only way the table can say who is up to date, because only the renderer
//    knows what the HTML hashes to. So the sequence the screen invites is
//    preview, read one, push. That is also why automatic pushes ship OFF: a bad
//    render reaching ten mailboxes is not a thing you can recall.
//
// 2. WHAT IT SHOWS IS WHAT GMAIL GETS. The preview is the exact HTML the
//    function would PATCH, in a sandboxed iframe — not a re-implementation of
//    the layout in our own CSS, which would look right here and wrong in
//    Outlook. The "logo blocked" toggle strips the image src so you can read the
//    alt-text state every corporate mail client shows by default.
//
// Owner and Director only, matched to the rules. Finance is senior on the Team
// page but has no business rewriting what the practice says in its own name.

const MONO = 'font-family:var(--font-technical)';
const CELL = 'padding:13px 12px;font-size:13px;color:var(--ink);border-bottom:1px solid var(--hair-soft)';
const HEAD = 'padding:0 12px 10px;font-size:10px;font-weight:500;letter-spacing:0.13em;text-transform:uppercase;color:var(--ink-3);text-align:left';
const BTN = 'padding:9px 16px;min-height:38px;border-radius:9px;border:1px solid var(--hair);background:var(--surface);font-family:inherit;font-size:12.5px;color:var(--ink);cursor:pointer';
const BTN_ON = 'padding:9px 16px;min-height:38px;border-radius:9px;border:1px solid var(--ink);background:var(--ink-fill);font-family:inherit;font-size:12.5px;color:var(--ink-fill-ink);cursor:pointer';
const GRID = 'grid-template-columns:34px 1.5fr 1.3fr 1.1fr 60px 1fr 1.4fr';

const when = (v) => {
  if (!v) return '—';
  const d = v && typeof v.toDate === 'function' ? v.toDate() : new Date(v);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
    : '—';
};

/**
 * The status one row shows.
 *
 * Before a dry run there is nothing honest to say beyond what the record holds,
 * so it says that: pushed on a date, or never. After a dry run it can say
 * whether what Gmail holds still matches what Track would send.
 */
function statusOf(user, dry) {
  const sig = user.signature || {};
  if (sig.lastError) return { text: 'Last push failed', tone: 'var(--brand-orange)', detail: sig.lastError };
  if (!dry) {
    return sig.lastPushedAt
      ? { text: `Pushed ${when(sig.lastPushedAt)}`, tone: 'var(--ink-3)' }
      : { text: 'Never pushed', tone: 'var(--ink-3)' };
  }
  if (dry.result === 'skipped') return { text: `Skipped — ${dry.reason}`, tone: 'var(--ink-3)' };
  if (dry.result === 'error') return { text: 'Will not render', tone: 'var(--brand-orange)', detail: dry.error };
  if (dry.result === 'unchanged') return { text: 'Up to date', tone: 'var(--brand-blue)' };
  return { text: sig.lastPushedHash ? 'Changed since last push' : 'Not in Gmail yet', tone: 'var(--brand-orange)' };
}

// v3 (31/10/2026): the preview frame and its blocked-image stand-ins come from
// public/signature-render.js — the same file the push renders with.

function openPreview(user, dry, onEdit) {
  const ov = openOverlay({
    title: titleCase(user.displayName || user.uid),
    subtitle: dry && dry.html ? 'Exactly the HTML that goes into Gmail.' : 'Run Preview all to render this one.',
    actionsHtml: `<button data-edit style="${BTN}">Edit details</button>`,
  });
  ov.content.parentElement.querySelector('[data-edit]')?.addEventListener('click', () => { ov.close(); onEdit(); });
  if (!dry || !dry.html) {
    ov.content.innerHTML = `<p class="bm-body" style="padding:18px 4px;color:var(--ink-3)">
      ${dry && dry.reason ? escapeHtml(`Skipped — ${dry.reason}`) : 'Nothing rendered yet.'}</p>`;
    return;
  }
  ov.content.innerHTML = `
    <div style="display:flex;align-items:center;gap:14px;padding:4px 0 16px">
      <label style="display:flex;align-items:center;gap:8px;font-size:12.5px;color:var(--ink)">Show it as
        <select data-block>${IMAGE_STATES.map((st) => `<option value="${st.key}">${escapeHtml(st.label)}</option>`).join('')}</select>
      </label>
      <span style="flex:1"></span>
      <span style="${MONO};font-size:11.5px;color:var(--ink-3)">${escapeHtml(String(dry.hash || '').slice(0, 12))}</span>
    </div>
    <iframe data-frame sandbox="" title="Signature preview"
      style="width:100%;min-height:220px;border:1px solid var(--hair);border-radius:12px;background:var(--surface);display:block"></iframe>
    ${(dry.missing || []).length ? `<p class="bm-body" style="margin-top:14px;color:var(--brand-orange);font-size:12.5px">
      Missing: ${escapeHtml(dry.missing.join(', '))}.</p>` : ''}`;

  const frame = ov.content.querySelector('[data-frame]');
  frame.style.height = '250px';      // fully sandboxed: no measuring inside it
  const paint = (state) => { frame.srcdoc = previewDocument(dry.html, { state }); };
  ov.content.querySelector('[data-block]').addEventListener('change', (e) => paint(e.target.value));
  paint('on');
}

/**
 * The Signature tab.
 *
 * @param mount    the panel element the Team page hands over
 * @param session  { email, displayName, roles }
 */
export async function renderSignatureTab(mount, session) {
  const roles = session.roles || [];
  const isOwner = roles.includes('Owner');
  mount.innerHTML = `<div id="sg-loading" style="margin-top:18px">${skeleton()}</div>`;

  let users = []; let cfg = null;
  try {
    [users, cfg] = await Promise.all([allUsers(), orgSignatureConfig().catch(() => null)]);
  } catch (e) {
    mount.innerHTML = `<p class="bm-body error" style="padding:16px 4px">Couldn’t load: ${escapeHtml(e.message || String(e))}</p>`;
    return;
  }
  users = users.filter((u) => u.active !== false).sort((a, b) => titleCase(a.displayName || a.id).localeCompare(titleCase(b.displayName || b.id)));

  let dryById = null;                       // uid → dry-run result, after Preview all
  const selected = new Set();
  let busy = false;

  const paint = () => {
    const missingTitle = users.filter((u) => !String(u.jobTitle || '').trim()).length;
    const off = users.filter((u) => u.signatureEnabled === false).length;
    const auto = cfg && cfg.autoPush === true;

    const brief = !cfg
      ? 'Track has no signature settings yet, so nothing can be rendered. Seed them with scripts/seed-signature-config.py, then reload.'
      : `${users.length} active ${users.length === 1 ? 'person' : 'people'}. `
        + (missingTitle ? `${missingTitle} ${missingTitle === 1 ? 'has' : 'have'} no role and will be skipped until one is set. ` : 'Everyone has a role set. ')
        + (off ? `${off} turned off. ` : '')
        + (auto
          ? 'Automatic pushes are ON: a change to a name, title, mobile or post-nominals updates Gmail on its own.'
          : 'Automatic pushes are OFF: nothing reaches Gmail until somebody pushes it here.');

    mount.innerHTML = `
      <div class="bm-eyebrow grey" style="margin-top:20px">EMAIL SIGNATURE</div>
      <p class="bm-body" style="max-width:74ch;margin:8px 0 20px;font-size:13.5px;line-height:1.55;color:${cfg ? 'var(--ink-2)' : 'var(--brand-orange)'}">${escapeHtml(brief)}</p>

      <div style="display:flex;flex-wrap:wrap;align-items:center;gap:10px;margin-bottom:22px">
        <button data-act="preview" style="${BTN_ON}">Preview all</button>
        <button data-act="push-selected" style="${BTN}">Push selected</button>
        <button data-act="push-all" style="${BTN}">Push all</button>
        <span style="flex:1"></span>
        ${isOwner ? `<button data-act="settings" style="${BTN}">Signature settings</button>` : ''}
        <span data-note style="${MONO};font-size:11.5px;color:var(--ink-3)"></span>
      </div>

      <p class="bm-body" style="margin:0 0 12px;font-size:12px;color:var(--ink-3)">A name opens the preview; a role or mobile opens the record to edit.</p>
      <div style="display:grid;${GRID};align-items:center">
        <span style="${HEAD}"></span>
        <span style="${HEAD}">Person</span>
        <span style="${HEAD}">Role</span>
        <span style="${HEAD}">Mobile</span>
        <span style="${HEAD}">On</span>
        <span style="${HEAD}">Last pushed</span>
        <span style="${HEAD}">Status</span>
        ${users.map((u) => {
    const dry = dryById ? dryById[u.uid || u.id] : null;
    const st = statusOf(u, dry);
    const id = u.id || u.uid;
    const noTitle = !String(u.jobTitle || '').trim();
    const noMobile = !String(u.mobile || '').trim();
    return `
          <span style="${CELL};text-align:center"><input type="checkbox" data-pick="${escapeHtml(id)}"${selected.has(id) ? ' checked' : ''} aria-label="Select ${escapeHtml(titleCase(u.displayName || id))}"></span>
          <span style="${CELL};display:flex;align-items:center;gap:10px;cursor:pointer" data-row="${escapeHtml(id)}">
            <span style="width:26px;height:26px;flex:none;border-radius:50%;background:${escapeHtml(u.iconColor || 'var(--brand-blue)')};color:var(--brand-white);display:flex;align-items:center;justify-content:center;font-size:10.5px">${escapeHtml(initials(u.displayName || id))}</span>
            ${escapeHtml(titleCase(u.displayName || id))}
          </span>
          <span style="${CELL};cursor:pointer;${noTitle ? 'color:var(--brand-orange)' : ''}" data-edit-row="${escapeHtml(id)}" title="Edit ${escapeHtml(titleCase(u.displayName || id))}\u2019s details">${escapeHtml(u.jobTitle || 'No job title')}</span>
          <span style="${CELL};${MONO};font-size:12.5px;cursor:pointer;${noMobile ? 'color:var(--ink-3)' : ''}" data-edit-row="${escapeHtml(id)}">${escapeHtml(u.mobile || 'none')}</span>
          <span style="${CELL};text-align:center"><input type="checkbox" data-on="${escapeHtml(id)}"${u.signatureEnabled === false ? '' : ' checked'} aria-label="Signature on for ${escapeHtml(titleCase(u.displayName || id))}"></span>
          <span style="${CELL};${MONO};font-size:12.5px;color:var(--ink-3)">${escapeHtml(when((u.signature || {}).lastPushedAt))}</span>
          <span style="${CELL};color:${st.tone}" ${st.detail ? `title="${escapeHtml(st.detail)}"` : ''}>${escapeHtml(st.text)}</span>`;
  }).join('')}
      </div>`;
    wire();
  };

  const note = (msg, bad = false) => {
    const n = mount.querySelector('[data-note]');
    if (!n) return;
    n.textContent = msg || '';
    n.style.color = bad ? 'var(--brand-orange)' : 'var(--ink-3)';
  };

  /** Every push and dry run comes through here, so "busy" means one thing. */
  const run = async ({ dryRun, uids, label }) => {
    if (busy) return;
    busy = true;
    note(dryRun ? 'Rendering…' : 'Pushing…');
    try {
      const res = await pushSignatures({ dryRun, uids: uids || null });
      if (dryRun) {
        dryById = Object.fromEntries(res.results.map((r) => [r.uid, r]));
      } else {
        // Re-read: the function has just written lastPushedAt/Hash onto the
        // records, and a stale table under a finished push is how somebody
        // pushes twice.
        users = (await allUsers()).filter((u) => u.active !== false)
          .sort((a, b) => titleCase(a.displayName || a.id).localeCompare(titleCase(b.displayName || b.id)));
        dryById = null;
      }
      const c = res.counts || {};
      const summary = Object.entries(c).map(([k, n]) => `${n} ${k}`).join(', ') || 'nothing to do';
      paint();
      note(`${label}: ${summary}`);
    } catch (e) {
      // The callable refuses for exactly two interesting reasons — wrong role,
      // or config not seeded — and both are worth reading rather than a shrug.
      note(e.message || String(e), true);
    } finally { busy = false; }
  };

  const confirmPush = (uids) => {
    const n = uids ? uids.length : users.length;
    openDialog({
      title: n === 1 ? 'Push this signature to Gmail?' : `Push ${n} signatures to Gmail?`,
      subtitle: 'Track replaces the signature on each person’s primary send-as address. Their own edits in Gmail are overwritten.',
      consequence: dryById
        ? 'You have a preview of what will be written. Anyone missing a role is skipped, not pushed blank.'
        : 'You have not previewed these. Preview all renders every signature without writing anything — worth doing first.',
      fields: [],
      primary: {
        label: n === 1 ? 'Push it' : `Push ${n}`,
        onSubmit: async () => { await run({ dryRun: false, uids, label: 'Pushed' }); },
      },
    });
  };

  /**
   * The three fields a signature is built from, on the canonical edit panel.
   *
   * This is where the practice's ten people actually get filled in, so it is
   * reachable from the row and from the preview rather than being buried on the
   * person record. A job title is required and marked so; the other two are
   * genuinely optional and say as much.
   */
  function openPersonEdit(u) {
    const id = u.id || u.uid;
    openPanel({
      title: titleCase(u.displayName || id),
      subtitle: 'What this person\u2019s email signature says about them.',
      fields: [
        {
          key: 'jobTitle', label: 'Role', type: 'text', span: true, required: true,
          value: u.jobTitle || '', placeholder: 'e.g. Senior Structural Engineer',
          help: 'Rendered in capitals under their name. Without one, they are skipped.',
        },
        {
          key: 'preNominals', label: 'Pre-nominals', type: 'text', optional: true,
          value: u.preNominals || '', placeholder: 'e.g. Eur Ing',
          help: 'Before the name, in its own case.',
        },
        {
          key: 'postNominals', label: 'Post-nominals', type: 'text', optional: true,
          value: u.postNominals || '', placeholder: 'e.g. BSc (Hons)',
          help: 'Follows the name, in its own case.',
        },
        {
          key: 'mobile', label: 'Mobile', type: 'text', optional: true,
          value: u.mobile || '', placeholder: '+44 7700 900123',
          help: 'Type it any way you like — it is stored as +44 7700 900123. Left out entirely when blank.',
        },
        {
          key: 'officeTel', label: 'Office number', type: 'text', optional: true,
          value: u.officeTel || '', placeholder: (cfg && cfg.officeTel) || '',
          help: 'Only if theirs is not the company number (e.g. the India office). With the country code.',
        },
      ],
      primary: {
        label: 'Save',
        onSubmit: async (v) => {
          // Canonicalised on the way IN, so the stored value is the one true
          // shape and every reader of the record — the renderer, the roster,
          // the next person to edit it — sees the same thing.
          const mobile = ukMobile(v.mobile);
          // v3: mobileE164 is derived here, on write (null when it can't be).
          const officeTel = String(v.officeTel || '').trim();
          await updateUserFields(id, {
            jobTitle: v.jobTitle, preNominals: v.preNominals || '', postNominals: v.postNominals, mobile, mobileE164: toE164(mobile) || null, officeTel,
            updatedBy: session.email, updatedAt: new Date().toISOString(),
          });
          Object.assign(u, { jobTitle: v.jobTitle, preNominals: v.preNominals || '', postNominals: v.postNominals, mobile, officeTel });
          // What Track would now send has changed, so the previous dry run is
          // no longer the truth about this row.
          if (dryById) delete dryById[id];
          paint();
          note(`Saved. ${titleCase(u.displayName || id)} needs a push.`);
        },
      },
    });
  }

  function openSettings() {
    const c = cfg || {};
    openPanel({
      title: 'Signature settings',
      // v3 (31/10/2026): the words themselves — logo, legal lines, numbers —
      // moved to Settings › Email signature, beside the live preview. What stays
      // here are the switches for how pushes happen.
      subtitle: 'How pushes happen. The words, the logo and the legal lines are in Settings › Email signature.',
      fields: [
        { key: 'skipEmails', label: 'Never touch these mailboxes', type: 'textarea', span: true, value: (c.skipEmails || []).join('\n'), help: 'One address per line — shared mailboxes that keep their own signature.' },
        { key: 'autoPush', label: 'Push automatically when a record changes', type: 'switch', span: true, value: c.autoPush === true, help: 'Off until you have read a preview and pushed one by hand.' },
        { key: 'autoVacation', label: 'Set out-of-office from approved leave', type: 'switch', span: true, value: c.autoVacation === true, help: 'Approving a holiday writes that person’s auto-reply, dated to the days they are away. A responder somebody set themselves is never overwritten.' },
      ],
      primary: {
        label: 'Save settings',
        onSubmit: async (v) => {
          const next = {
            skipEmails: String(v.skipEmails || '').split('\n').map((s) => s.trim()).filter(Boolean),
            autoPush: v.autoPush === true,
            autoVacation: v.autoVacation === true,
          };
          await saveOrgSignature(next);
          cfg = { ...c, ...next };
          paint();
          note('Settings saved. Nothing was pushed.');
        },
      },
    });
  }

  function wire() {
    mount.querySelectorAll('[data-row]').forEach((el) => el.addEventListener('click', () => {
      const id = el.dataset.row;
      const u = users.find((x) => (x.id || x.uid) === id);
      if (u) openPreview({ ...u, uid: id }, dryById ? dryById[id] : null, () => openPersonEdit(u));
    }));
    mount.querySelectorAll('[data-edit-row]').forEach((el) => el.addEventListener('click', () => {
      const u = users.find((x) => (x.id || x.uid) === el.dataset.editRow);
      if (u) openPersonEdit(u);
    }));
    mount.querySelectorAll('[data-pick]').forEach((el) => el.addEventListener('change', () => {
      if (el.checked) selected.add(el.dataset.pick); else selected.delete(el.dataset.pick);
    }));
    mount.querySelectorAll('[data-on]').forEach((el) => el.addEventListener('change', async () => {
      const id = el.dataset.on;
      const on = el.checked;
      try {
        await updateUserFields(id, { signatureEnabled: on, updatedBy: session.email, updatedAt: new Date().toISOString() });
        const u = users.find((x) => (x.id || x.uid) === id);
        if (u) u.signatureEnabled = on;
        note(`${on ? 'On' : 'Off'} for ${titleCase((u && u.displayName) || id)}.`);
      } catch (e) {
        el.checked = !on;               // the record did not change, so nor does the box
        note(e.message || String(e), true);
      }
    }));
    mount.querySelector('[data-act="preview"]')?.addEventListener('click', () => run({ dryRun: true, label: 'Previewed' }));
    mount.querySelector('[data-act="push-all"]')?.addEventListener('click', () => confirmPush(null));
    mount.querySelector('[data-act="push-selected"]')?.addEventListener('click', () => {
      if (!selected.size) { note('Nothing selected.'); return; }
      confirmPush([...selected]);
    });
    mount.querySelector('[data-act="settings"]')?.addEventListener('click', openSettings);
  }

  paint();
}
